import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogTitle, LinearProgress } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import EditDialogStyle from "styles/application/EditDialogStyle";
import { getLocalStorageData } from "helpers/utils";
import { showError } from "../../../../../redux/common/actions";
import { addSiteAsset, editSiteAsset } from "services/clients/sites/siteAssets";
import { updateSiteAssetReferences, postSiteAssetReferences } from "services/clients/sites/siteAssets/references";

const ET = EditDialogStyle();

const useStyles = makeStyles()(() => ({
  paper: { minWidth: "90%" },
}));

const initialState = {
  assetId: null,
  assetNo: "",
  description: "",
  referenceName: "",
  plannerGroup: "",
  workCenter: "",
};

const AssetDialog = ({
  open,
  closeHandler,
  onSuccess,
  getError,
  isEdit = false,
  isSAPMode = false,
  siteId,
  data = {},
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { customCaptions } = getLocalStorageData("me");
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState(initialState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setInput({ ...initialState, ...data });
      setErrors({});
    }
  }, [open, data]);

  const closeOverride = () => {
    closeHandler();
    setInput(initialState);
    setErrors({});
  };

  const validate = () => {
    const nextErrors = {};

    if (!isSAPMode) {
      if (!input.assetNo?.trim()) {
        nextErrors.assetNo = `${customCaptions?.asset || "Asset"} number is required`;
      }
      if (!input.description?.trim()) {
        nextErrors.description = "Description is required";
      }
    } else {
      if (!input.referenceName?.trim()) {
        nextErrors.referenceName = `${customCaptions?.assetReference || "FLOC"} is required`;
      }
      if (!input.description?.trim()) {
        nextErrors.description = "Description is required";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleEnterPress = (e) => {
    if (e.keyCode === 13) {
      handleSave();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setErrors({});
    try {
      if (!validate()) {
        setLoading(false);
        return;
      }

      let result;

      if (isEdit) {
        if (isSAPMode && input.referenceId) {
          // Update Reference using RFC 6902 format
          const ops = [
            { op: "replace", path: "assetName", value: input.assetNo },
            { op: "replace", path: "name", value: input.referenceName },
            { op: "replace", path: "description", value: input.description },
            { op: "replace", path: "plannerGroup", value: input.plannerGroup || null },
            { op: "replace", path: "workCenter", value: input.workCenter || null },
          ];
          result = await updateSiteAssetReferences(input.referenceId, ops);
          result = { success: result.status, data: result.data };
        } else {
          // Update Asset (non-SAP)
          const ops = [
            { op: "replace", path: "name", value: input.assetNo },
            { op: "replace", path: "description", value: input.description },
          ];
          const targetId = input.assetId;
          result = await editSiteAsset(targetId, ops);
          result = { success: result.status, data: result.data };
        }
      } else {
        if (isSAPMode) {
          // Create Reference for Site
          const payload = {
            siteId: +siteId,
            assetName: input.assetNo,
            name: input.referenceName,
            description: input.description,
            plannerGroup: input.plannerGroup || null,
            workCenter: input.workCenter || null,
          };
          const resp = await postSiteAssetReferences(payload);
          result = { success: resp.status, data: resp.data };
        } else {
          // Create Asset
          const resp = await addSiteAsset({
            siteId: +siteId,
            name: input.assetNo,
            description: input.description,
          });
          result = { success: resp.success ?? resp.status, data: resp.data, errors: resp.errors };
        }
      }

      if (result.success) {
        await onSuccess();
        closeOverride();
      } else {
        if (result.data?.detail) {
          getError(result.data.detail);
        } else if (result.errors) {
          setErrors(result.errors);
        } else {
          getError("Failed to save");
        }
      }
    } catch (err) {
      dispatch(showError("Failed to save"));
    } finally {
      setLoading(false);
    }
  };

  const dialogTitle = useMemo(() => {
    const assetLabel = customCaptions?.asset || "Asset";
    const assetRefLabel = customCaptions?.assetReference || "Asset Reference";
    if (isEdit) {
      return isSAPMode ? `Edit ${assetRefLabel}` : `Edit ${assetLabel}`;
    }
    return isSAPMode ? `Add ${assetRefLabel}` : `Add New ${assetLabel}`;
  }, [isEdit, isSAPMode, customCaptions]);

  // Build field sets
  const assetFields = useMemo(() => {
    if (isSAPMode) {
      return [
        { name: "assetNo", label: customCaptions?.asset || "Asset", required: false, readOnly: false },
      ];
    }
    return [
      { name: "assetNo", label: customCaptions?.asset || "Asset", required: true },
      { name: "description", label: "Description", required: true },
    ];
  }, [isSAPMode, customCaptions]);

  const referenceFields = useMemo(() => {
    if (!isSAPMode) return [];
    return [
      { name: "referenceName", label: customCaptions?.assetReference || "FLOC", required: true },
      { name: "description", label: "Description", required: true },
      { name: "plannerGroup", label: customCaptions?.assetPlannerGroup || "Planner Group", required: false },
      { name: "workCenter", label: customCaptions?.assetMainWorkCenter || "Work Center", required: false },
    ];
  }, [isSAPMode, customCaptions]);

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} onClose={closeOverride} aria-labelledby="asset-dialog-title">
      {loading && <LinearProgress />}
      <ET.ActionContainer>
        <DialogTitle id="asset-dialog-title">
          <ET.HeaderText>{dialogTitle}</ET.HeaderText>
        </DialogTitle>
        <ET.ButtonContainer>
          <ET.CancelButton onClick={closeOverride} variant="contained">Cancel</ET.CancelButton>
          <ET.ConfirmButton variant="contained" onClick={handleSave}>{isEdit ? "Save" : "Add"}</ET.ConfirmButton>
        </ET.ButtonContainer>
      </ET.ActionContainer>

      <ET.DialogContent>
{isSAPMode ? (
          <>
            {/* Top Row - Asset field only */}
            <ET.InputContainer>
              <ET.LeftInputContainer>
                <div style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}>
                  <ET.NameLabel>
                    {assetFields[0].label}
                    {assetFields[0].required && <ET.RequiredStar>*</ET.RequiredStar>}
                  </ET.NameLabel>
                  <ET.NameInput
                    name={assetFields[0].name}
                    error={Boolean(errors?.[assetFields[0].name])}
                    helperText={errors?.[assetFields[0].name] || null}
                    variant="outlined"
                    value={input?.[assetFields[0].name] ?? ""}
                    disabled={assetFields[0].readOnly}
                    onChange={handleChange}
                    onKeyDown={handleEnterPress}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#000000",
                      },
                    }}
                  />
                </div>
              </ET.LeftInputContainer>
              <ET.RightInputContainer>
                {/* Empty space */}
              </ET.RightInputContainer>
            </ET.InputContainer>

            {/* Bottom 2x2 Grid */}
            <ET.InputContainer>
              <ET.LeftInputContainer>
                {[referenceFields[0], referenceFields[2]].map((field) => (
                  <div key={field.name} style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}>
                    <ET.NameLabel>
                      {field.label}
                      {field.required && <ET.RequiredStar>*</ET.RequiredStar>}
                    </ET.NameLabel>
                    <ET.NameInput
                      name={field.name}
                      error={Boolean(errors?.[field.name])}
                      helperText={errors?.[field.name] || null}
                      variant="outlined"
                      value={input?.[field.name] ?? ""}
                      disabled={field.readOnly}
                      onChange={handleChange}
                      onKeyDown={handleEnterPress}
                      fullWidth
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "#000000",
                        },
                      }}
                    />
                  </div>
                ))}
              </ET.LeftInputContainer>
              <ET.RightInputContainer>
                {[referenceFields[1], referenceFields[3]].map((field) => (
                  <div key={field.name} style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}>
                    <ET.NameLabel>
                      {field.label}
                      {field.required && <ET.RequiredStar>*</ET.RequiredStar>}
                    </ET.NameLabel>
                    <ET.NameInput
                      name={field.name}
                      error={Boolean(errors?.[field.name])}
                      helperText={errors?.[field.name] || null}
                      variant="outlined"
                      value={input?.[field.name] ?? ""}
                      disabled={field.readOnly}
                      onChange={handleChange}
                      onKeyDown={handleEnterPress}
                      fullWidth
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "#000000",
                        },
                      }}
                    />
                  </div>
                ))}
              </ET.RightInputContainer>
            </ET.InputContainer>
          </>
        ) : (
          <ET.InputContainer>
            <ET.LeftInputContainer>
              {(() => {
                const allFields = [...assetFields, ...referenceFields];
                const leftFields = allFields.slice(0, Math.ceil(allFields.length / 2));
                return leftFields.map((field) => {
                  const { name, label, required, readOnly } = field;
                  return (
                    <div key={name} style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}>
                      <ET.NameLabel>
                        {label}
                        {required && <ET.RequiredStar>*</ET.RequiredStar>}
                      </ET.NameLabel>
                      <ET.NameInput
                        name={name}
                        error={Boolean(errors?.[name])}
                        helperText={errors?.[name] || null}
                        variant="outlined"
                        value={input?.[name] ?? ""}
                        disabled={readOnly}
                        onChange={handleChange}
                        onKeyDown={handleEnterPress}
                        fullWidth
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#000000",
                          },
                        }}
                      />
                    </div>
                  );
                });
              })()}
            </ET.LeftInputContainer>
            <ET.RightInputContainer>
              {(() => {
                const allFields = [...assetFields, ...referenceFields];
                const rightFields = allFields.slice(Math.ceil(allFields.length / 2));
                return rightFields.map((field) => {
                  const { name, label, required, readOnly } = field;
                  return (
                    <div key={name} style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}>
                      <ET.NameLabel>
                        {label}
                        {required && <ET.RequiredStar>*</ET.RequiredStar>}
                      </ET.NameLabel>
                      <ET.NameInput
                        name={name}
                        error={Boolean(errors?.[name])}
                        helperText={errors?.[name] || null}
                        variant="outlined"
                        value={input?.[name] ?? ""}
                        disabled={readOnly}
                        onChange={handleChange}
                        onKeyDown={handleEnterPress}
                        fullWidth
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#000000",
                          },
                        }}
                      />
                    </div>
                  );
                });
              })()}
            </ET.RightInputContainer>
          </ET.InputContainer>
        )}
      </ET.DialogContent>
    </Dialog>
  );
};

AssetDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  closeHandler: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  getError: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  isSAPMode: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  data: PropTypes.object,
};

export default AssetDialog;
